import React, { useState } from "react";
import { FiMenu } from "react-icons/fi";
import { MdCancel, MdOutlineLogout } from "react-icons/md";
import { RiLockPasswordFill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { NavItemType } from "../../../navigation";
import { Tooltip } from "@mui/material";
import { BiChevronRight } from "react-icons/bi";
import { setExpandedIndex } from "src/redux/slices/SideNavLayoutSlice";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/redux/store";
import ChangePasswordFormWrapper from "../Header/UserProfileCard/ChangePassword/ChangePasswordFormWrapper";
import { clearLocalStorage } from "src/utils/auth/authUtils";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { LOGIN_PAGE_NAME } from "src/utils/constants";

const brandName = LOGIN_PAGE_NAME?.trim() || "Carrer Prefix Billing";
const brandLogo = "/careerprefixfab.png";

type Props = {
  toggleCollapse: () => void;
  isCollapsed: boolean;
  navigation: NavItemType[];
  isPathEqualtoNavItem?: (navItem: any) => boolean;
};

const VerticalNavBar = ({
  toggleCollapse,
  isCollapsed,
  navigation,
  isPathEqualtoNavItem = () => false,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const sideNavLayoutState: any = useSelector(
    (state: RootState) => state.sideNavLayout
  );
  const [isOpenChangePasswordDialog, setIsOpenChangePasswordDialog] =
    useState(false);

  const { expandedIndex } = sideNavLayoutState;

  const collapsedTooltip = (title: string) => ({
    title,
    placement: "right" as const,
    arrow: true,
    disableHoverListener: !isCollapsed,
    disableFocusListener: !isCollapsed,
    disableTouchListener: !isCollapsed,
  });

  const matchesPath = (path?: string) => {
    if (!path) return false;
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const isChildActive = (
    child: { path?: string },
    siblings: { path?: string }[] = []
  ) => {
    if (!matchesPath(child.path)) return false;
    return !siblings.some(
      (other) =>
        other.path &&
        child.path &&
        other.path !== child.path &&
        other.path.length > child.path.length &&
        matchesPath(other.path)
    );
  };

  const isNavActive = (navItem: any) => {
    if (navItem.children?.length) {
      return navItem.children.some((child: { path?: string }) =>
        isChildActive(child, navItem.children)
      );
    }
    return matchesPath(navItem.path) || isPathEqualtoNavItem(navItem);
  };

  return (
    <div
      className={`absolute z-[1] flex h-full flex-col overflow-hidden border-r border-white/10 bg-primary-dark shadow-[8px_0_30px_rgba(6,27,51,0.12)] transition-all duration-300 ease-out md:static ${
        isCollapsed
          ? "w-0 min-w-0 md:w-[72px] md:min-w-[72px]"
          : "min-w-[260px] w-[260px]"
      }`}
    >
      <div
        className={`flex h-[64px] shrink-0 items-center border-b border-white/10 ${
          isCollapsed
            ? "justify-between px-4 md:justify-center md:px-0"
            : "justify-between px-4"
        }`}
      >
        <div
          className={`flex min-w-0 items-center gap-2.5 ${
            isCollapsed && "md:hidden"
          }`}
        >
          <img
            src={brandLogo}
            alt={brandName}
            className="h-9 w-9 rounded-xl bg-white object-contain p-0.5"
          />
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold leading-5 text-white">
              {brandName}
            </div>
            <div className="text-[11px] font-medium tracking-wide text-white/60">
              Billing
            </div>
          </div>
        </div>

        <Tooltip title="Alt + T">
          <button
            type="button"
            onClick={toggleCollapse}
            className="no-btn-motion rounded-xl p-2 text-xl text-white transition-colors duration-200 hover:bg-white/10"
          >
            <FiMenu className="hidden md:block" />
            <MdCancel className="block md:hidden text-xl" />
          </button>
        </Tooltip>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
        {navigation?.map((navItem: any, navIndex: number) => {
          const parentActive = isNavActive(navItem);
          return (
            <React.Fragment key={navIndex}>
              <Tooltip {...collapsedTooltip(navItem.label)}>
                <div
                  onClick={() =>
                    navItem.path && !navItem.children
                      ? navigate({
                          pathname: navItem.path,
                        })
                      : dispatch(
                          setExpandedIndex(
                            expandedIndex === navIndex ? -1 : navIndex
                          )
                        )
                  }
                  style={{ animationDelay: `${navIndex * 35}ms` }}
                  className={`
                side-nav-item
                animate-nav-slide
                relative
                mx-2
                my-0.5
                shrink-0
                cursor-pointer
                rounded-xl
                px-3
                py-2.5
                text-sm
                font-medium
                text-white/90
                transition-all
                duration-200
                ${isCollapsed ? "md:mx-1.5 md:px-0" : ""}
                ${!parentActive && "hover:bg-white/10 hover:text-white"}
                ${
                  parentActive
                    ? "bg-white/10 font-semibold text-white shadow-[inset_3px_0_0_#e4ab1f]"
                    : ""
                }
                `}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "md:justify-center" : "justify-between"
                    }`}
                  >
                    <div
                      className={`flex items-center ${
                        isCollapsed ? "md:justify-center" : "gap-3"
                      }`}
                    >
                      <navItem.icon className="shrink-0 text-xl" />
                      {!isCollapsed && <div>{navItem.label}</div>}
                    </div>
                    {navItem.children && !isCollapsed && (
                      <div>
                        <BiChevronRight
                          className={`text-2xl text-white transition-transform duration-300 ${
                            expandedIndex === navIndex && "rotate-90"
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
              {expandedIndex === navIndex && navItem.children && (
                <div
                  className={
                    isCollapsed
                      ? "mb-1 ml-4 mr-1 flex shrink-0 flex-col items-end gap-0.5 rounded-xl bg-black/25 py-1 pr-0.5"
                      : "animate-submenu-reveal flex shrink-0 flex-col gap-0.5 px-3 pb-2"
                  }
                >
                  {navItem.children?.map(
                    (navChild: any, navChildIndex: number) => {
                      const childActive = isChildActive(
                        navChild,
                        navItem.children
                      );
                      return (
                        <Tooltip
                          key={navChildIndex}
                          {...collapsedTooltip(navChild.label)}
                        >
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              navChild.path &&
                                navigate({
                                  pathname: navChild.path,
                                });
                            }}
                            id={childActive ? "scroll-to-view" : ""}
                            style={{
                              animationDelay: `${navChildIndex * 30}ms`,
                            }}
                            className={`
              animate-nav-slide
              shrink-0
              cursor-pointer
              rounded-lg
              text-[14px]
              transition-all
              duration-200
              hover:bg-white/10
              ${
                isCollapsed
                  ? "flex h-9 w-9 items-center justify-center"
                  : ""
              }
              ${
                childActive
                  ? "bg-white/10 font-semibold text-white shadow-[inset_3px_0_0_#e4ab1f]"
                  : "text-slate-300"
              }
              `}
                          >
                            {isCollapsed ? (
                              <navChild.icon className="shrink-0 text-lg" />
                            ) : (
                              <div className="my-0.5 flex items-center gap-3 px-3 py-2">
                                <navChild.icon className="shrink-0 text-lg" />
                                <div
                                  className={
                                    childActive
                                      ? "font-semibold text-white"
                                      : "text-slate-300"
                                  }
                                >
                                  {navChild.label}
                                </div>
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      );
                    }
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div
        className={`shrink-0 border-t border-white/10 bg-primary-dark px-2 pb-3 pt-2 ${
          isCollapsed ? "hidden md:block" : ""
        }`}
      >
        <Tooltip {...collapsedTooltip("Change Password")}>
          <button
            type="button"
            onClick={() => setIsOpenChangePasswordDialog(true)}
            className={`no-btn-motion flex w-full items-center rounded-xl py-2.5 text-sm text-slate-200 transition-all duration-200 hover:bg-white/10 hover:text-white ${
              isCollapsed ? "justify-center px-0" : "gap-3 px-3"
            }`}
          >
            <RiLockPasswordFill className="shrink-0 text-xl" />
            {!isCollapsed && "Change Password"}
          </button>
        </Tooltip>
        <Tooltip {...collapsedTooltip("Logout")}>
          <button
            type="button"
            onClick={() => {
              showConfirmationDialog({
                title: "Logout",
                text: "Are you sure you want to logout?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Logout",
                confirmButtonColor: "#082645",
                next: (result) => {
                  if (result?.isConfirmed) {
                    clearLocalStorage();
                    navigate("/");
                  }
                },
              });
            }}
            className={`no-btn-motion flex w-full items-center rounded-xl py-2.5 text-sm text-slate-200 transition-all duration-200 hover:bg-white/10 hover:text-white ${
              isCollapsed ? "justify-center px-0" : "gap-3 px-3"
            }`}
          >
            <MdOutlineLogout className="shrink-0 text-xl" />
            {!isCollapsed && "Logout"}
          </button>
        </Tooltip>
      </div>

      {isOpenChangePasswordDialog ? (
        <ChangePasswordFormWrapper
          onClose={() => setIsOpenChangePasswordDialog(false)}
        />
      ) : null}
    </div>
  );
};

export default VerticalNavBar;
